/**
 * The route tree. Each area's components load lazily from their own chunk
 * (`/areas/<area>/…`, see `vite.config.ts`); only the route definitions
 * (paths and loaders) are in the entry, so preloading on intent can fetch
 * a route's code and data before the click. Loaders fetch through the
 * shared query client (`lib/api/query.ts`), loaded on first use.
 */
import { createRootRoute, createRoute, Outlet } from '@tanstack/react-router';

import { AREA_NAV, AREA_PAGES, type AppArea } from '../areas/nav.js';
import { ErrorPage, NotFoundPage } from './NotFoundPage.js';

/** The root route: no layout of its own (each page picks its layout). */
export const rootRoute = createRootRoute({
  component: Outlet,
  notFoundComponent: NotFoundPage,
  errorComponent: ErrorPage,
});

const publicArea = () => import('../areas/public/routes.js');
const candidateArea = () => import('../areas/candidate/routes.js');
const schoolArea = () => import('../areas/school/routes.js');
const adminArea = () => import('../areas/admin/routes.js');
const accountArea = () => import('../areas/account/routes.js');

/**
 * `/` has no page of its own: it sends signed-out visitors to sign-in and
 * everyone else to their area (the guard loads with the API client).
 */
const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => import('../lib/session/session.js').then((session) => session.leaveHome()),
});

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  validateSearch: (search: Record<string, unknown>): { next?: string } =>
    typeof search.next === 'string' ? { next: search.next } : {},
}).lazy(() => publicArea().then((area) => area.signInRoute));

const verifyRoute = createRoute({ getParentRoute: () => rootRoute, path: '/auth/verify' }).lazy(
  () => publicArea().then((area) => area.verifyRoute),
);

const notMeRoute = createRoute({ getParentRoute: () => rootRoute, path: '/auth/not-me' }).lazy(() =>
  publicArea().then((area) => area.notMeRoute),
);

const welcomeRoute = createRoute({ getParentRoute: () => rootRoute, path: '/welcome' }).lazy(() =>
  publicArea().then((area) => area.welcomeRoute),
);

/** After "Connect WeChat" (H6.2): in WeChat's own browser, so public. */
const weChatDoneRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/connected/wechat',
}).lazy(() => publicArea().then((area) => area.weChatDoneRoute));

/** A secure link (H3.1): public, whoever is signed in; the secret is only in the fragment. */
const linkRoute = createRoute({ getParentRoute: () => rootRoute, path: '/l/$linkId' }).lazy(() =>
  publicArea().then((area) => area.linkRoute),
);

/**
 * Signed-in pages need a signed-in, active account: the guard (loaded with
 * the API client, and preloaded on intent with the page's code) sends
 * anyone else to sign in or to the "being set up" page.
 */
const requireAccount = (area: AppArea, path: string) =>
  import('../lib/session/session.js').then((session) => session.requireAccount(area, path));

function areaRoutes(area: AppArea, load: () => Promise<{ lazyRoute: (path: string) => unknown }>) {
  return [...AREA_NAV[area].map((entry) => entry.to), ...AREA_PAGES[area]].map((path) =>
    createRoute({
      getParentRoute: () => rootRoute,
      path,
      // `next`: where to go after a step; `via`: how a school came to a candidate (H4.2), or a
      // teacher to a school (H4.5).
      validateSearch: (
        search: Record<string, unknown>,
      ): { next?: string; via?: string; saved?: string } => ({
        ...(typeof search.next === 'string' ? { next: search.next } : {}),
        ...(typeof search.via === 'string' ? { via: search.via } : {}),
        // A saved search's alert link (H4.4).
        ...(typeof search.saved === 'string' ? { saved: search.saved } : {}),
      }),
      beforeLoad: ({ location }) => requireAccount(area, location.href),
    }).lazy(() => load().then((area) => area.lazyRoute(path) as never)),
  );
}

/** Whether the development gallery is built in (development, or `VITE_HIRE_GALLERY=true` for browser tests). */
export const GALLERY_ENABLED = import.meta.env.DEV || import.meta.env.VITE_HIRE_GALLERY === 'true';

const devRoutes = GALLERY_ENABLED
  ? [
      createRoute({ getParentRoute: () => rootRoute, path: '/dev/gallery' }).lazy(() =>
        import('../dev/routes.js').then((dev) => dev.galleryRoute),
      ),
    ]
  : [];

/** Every route. */
export const routeTree = rootRoute.addChildren([
  homeRoute,
  signInRoute,
  verifyRoute,
  notMeRoute,
  weChatDoneRoute,
  welcomeRoute,
  linkRoute,
  ...areaRoutes('candidate', candidateArea),
  ...areaRoutes('school', schoolArea),
  ...areaRoutes('admin', adminArea),
  ...areaRoutes('account', accountArea),
  ...devRoutes,
]);
